import Button from '@ixigo/iui/Button';
import Typography from '@ixigo/iui/Typography';
import type {
	ActionPopup,
	ActionPopupAction,
	ActionPopupHighlight,
} from '@/api/fareDetails';

export interface ActionPopupViewProps {
	popup: ActionPopup;
	/**
	 * Called with the chosen button's action descriptor; the caller resolves it
	 * to a behavior (refetch / edit / dismiss). The view owns which button maps
	 * to primary vs secondary.
	 */
	onAction: (action: ActionPopupAction['action']) => void;
}

/** Dotted line with endpoint dots connecting the two route labels. */
function RouteConnector() {
	return (
		<div className="flex flex-1 items-center">
			<span className="size-10 shrink-0 rounded-full bg-neutral-200" />
			<span className="flex-1 border-t border-dashed border-secondary" />
			<span className="size-10 shrink-0 rounded-full bg-neutral-200" />
		</div>
	);
}

/** Route strip from the popup highlights: label (short) over value (full name) */
function HighlightsRoute({
	from,
	to,
}: {
	from: ActionPopupHighlight;
	to: ActionPopupHighlight;
}) {
	return (
		<div className="bg-tertiary rounded-10 px-15px pt-10 pb-15px">
			<div className="flex items-center gap-5px pt-5px">
				<Typography
					variant="body-md"
					style={{ maxWidth: '42%' }}
					className="font-medium truncate"
				>
					{from.label}
				</Typography>
				<RouteConnector />
				<Typography
					variant="body-md"
					style={{ maxWidth: '42%' }}
					className="font-medium truncate text-right"
				>
					{to.label}
				</Typography>
			</div>
			<div className="flex gap-20">
				<Typography variant="body-xs" className="flex-1 truncate">
					{from.value}
				</Typography>
				<Typography variant="body-xs" className="flex-1 truncate text-right">
					{to.value}
				</Typography>
			</div>
		</div>
	);
}

/**
 * Renders a server-driven action popup in the cab-selection sheet body.
 * Content (title, copy, highlights, button labels) is entirely backend-
 * supplied; the sheet provides the header + container.
 */
export function ActionPopupView({ popup, onAction }: ActionPopupViewProps) {
	const [from, to] = popup.highlights;
	const { primary, secondary } = popup.actions;

	return (
		<div className="flex flex-col gap-10 p-20">
			{from && to && <HighlightsRoute from={from} to={to} />}
			<Typography
				variant="body-sm"
				className="text-center text-balance"
				// biome-ignore lint/security/noDangerouslySetInnerHtml: backend-controlled confirmation copy
				dangerouslySetInnerHTML={{ __html: popup.message }}
			/>
			<div className="flex gap-20 pt-10">
				{secondary && (
					<Button
						variant="soft"
						color="brand"
						size="lg"
						className="flex-1"
						onClick={() => onAction(secondary.action)}
					>
						{secondary.text}
					</Button>
				)}
				<Button
					variant="solid"
					color="brand"
					size="lg"
					className="flex-1"
					onClick={() => onAction(primary.action)}
				>
					{primary.text}
				</Button>
			</div>
		</div>
	);
}
